
import React from 'react';
import { Stack } from '@mui/material';
import { PatientObj } from 'pages/Dashboard/types/patient.types';
import { INVITE_DRAWER_TYPE, PATIENT_LIST_VIEW } from 'pages/Dashboard/enums';
import ConfirmPatientDrawer from 'pages/Dashboard/components/ConfirmPatientDrawer';
import { PatientCard } from 'pages/Dashboard/components/PatientCard';
import { useNavigate } from 'react-router-dom';
import { mainNav } from 'utils/spaUrls';
import RecordRefreshDrawer from 'pages/Dashboard/RecordRefreshDrawer';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';

interface PatientCardListProps {
  patients: PatientObj[];
  status: PATIENT_LIST_VIEW;
}

function PatientCardList({ patients, status }: PatientCardListProps) {
  const navigator = useNavigate();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerPatient, setDrawerPatient] = React.useState<PatientObj | null>(null);
  const [showRecordRefreshDrawer, setShowRecordRefreshDrawer] = React.useState(false);
  const [selectedPatient, setSelectedPatient] = React.useState<PatientObj | null>(null);

  const handleConfirm = (patient: PatientObj) => {
    setDrawerOpen(true);
    setDrawerPatient(patient);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => {
      setDrawerPatient(null);
    }, 300);
  };
  const handleViewHealthRecords = (e: React.MouseEvent<HTMLButtonElement>, patientId?: string) => {
    e.preventDefault();
    e.stopPropagation();
    const url = mainNav.patientDetail(patientId || '');
    const state = { tab: 1 };
    if (e.metaKey || e.ctrlKey) {
      window.open(url, '_blank');
    }
    else {
      navigator(url, { state });
    }
  };

  const handlePendingPatientClick = (
    e: React.MouseEvent<HTMLDivElement>,
    shouldResendInvite: boolean,
    setDrawerItem: (item: INVITE_DRAWER_TYPE) => void,
  ) => {
    e.preventDefault();
    if (shouldResendInvite) {
      trackMixpanelEvent(MixpanelEventName.RESEND_INVITE_BUTTON_CLICKED, {
        location: 'signup_pending_tab',
      });
    }
    else {
      trackMixpanelEvent(MixpanelEventName.RESOLVE_ERROR_BUTTON_CLICKED, {
        location: 'invite_errors_tab',
      });
    }
    setDrawerItem(shouldResendInvite
      ? INVITE_DRAWER_TYPE.RESEND_INVITE
      : INVITE_DRAWER_TYPE.RESOLVE_INVITE);
  };

  const handleRefreshIconClick = (e: React.MouseEvent<HTMLDivElement>, patient?: PatientObj) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedPatient(patient || null);
    setShowRecordRefreshDrawer(true);
  };

  return (
    <>
      <ConfirmPatientDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        patient={drawerPatient ?? ({} as PatientObj)}
      />
      <RecordRefreshDrawer
        patientId={selectedPatient?.id as string}
        isOpen={showRecordRefreshDrawer}
        // isDemoPatient={selectedPatient?.demoPatient || false}
        isPendingApproval={selectedPatient?.approvalStatus === 'PATIENT_APPROVAL_STATUS_PENDING'}
        setShowRecordRefreshDrawer={setShowRecordRefreshDrawer}
      />
      <Stack spacing={2} sx={{ pt: 2 }}>
        {patients.map((patient) => {
          const isReady = status === PATIENT_LIST_VIEW.READY;
          const isPending = patient.approvalStatus === 'PATIENT_APPROVAL_STATUS_PENDING';
          const href = isReady && !isPending ? mainNav.patientDetail(patient.id || '') : undefined;
          return (
            <PatientCard
              key={patient.id || `${patient.name?.firstName} ${patient.name?.lastName}` || patient.alias}
              patient={patient}
              status={status}
              href={href}
              onConfirm={handleConfirm}
              onViewHealthRecords={handleViewHealthRecords}
              onRefreshIconClick={handleRefreshIconClick}
              onPendingPatientClick={handlePendingPatientClick}
            />
          );
        })}
      </Stack>
    </>
  );
}

export default PatientCardList;
